import { useEffect, useRef, useState, type ReactNode } from "react";
import { useCompactControls } from "../hooks";
import { Sheet } from "./Sheet";

/* A button that opens a panel anchored under it, and closes on a click outside
   or Escape.

   Extracted because the composer now leans on it for the three controls that
   used to be a 300px sidebar column: what makes those safe to hide is that they
   are one click away and dismiss the way every other transient thing on the
   page does. Getting that wrong in three places separately is how a panel ends
   up with a popover you cannot close.

   Under a coarse pointer the same content opens as a bottom sheet instead. It
   is one branch here rather than a mobile variant of each caller, which is what
   makes the voice picker, the model picker, the tuning sliders and the
   connections list all become thumb-reachable from a single change — and keeps
   them impossible to add a fifth one that forgot. */

interface Props {
  /** What the trigger shows. */
  label: ReactNode;
  /** Accessible name for the trigger, when the label is not enough on its own. */
  title?: string;
  className?: string;
  /** Popover width; the voice list wants more room than the model select.
      Ignored as a sheet, which is always the width of the screen. */
  width?: number;
  ariaLabel: string;
  children: (close: () => void) => ReactNode;
}

export function Popover({
  label,
  title,
  className = "",
  width,
  ariaLabel,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);
  const sheet = useCompactControls();
  const close = () => setOpen(false);

  useEffect(() => {
    // The sheet brings its own scrim and its own Escape; a document-level
    // mousedown listener on top of that closes it before the tap that opened
    // its content ever lands.
    if (!open || sheet) return;
    const onDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, sheet]);

  const trigger = (
    <button
      type="button"
      className={className}
      title={title}
      aria-expanded={open}
      aria-haspopup="dialog"
      onClick={() => setOpen((was) => !was)}
    >
      {label}
    </button>
  );

  if (sheet) {
    return (
      <span className="popover-wrap" ref={wrapper}>
        {trigger}
        <Sheet open={open} title={ariaLabel} onClose={close}>
          {children(close)}
        </Sheet>
      </span>
    );
  }

  return (
    <span className="popover-wrap" ref={wrapper}>
      {trigger}

      {open && (
        <div
          className="popover"
          role="dialog"
          aria-label={ariaLabel}
          style={width ? { width } : undefined}
        >
          {children(close)}
        </div>
      )}
    </span>
  );
}
